import React, {useEffect, useRef, useState, useCallback} from 'react'

import {
  Wrapper,
  Dropdown,
  LoadingState,
  EmptyState,
  DropdownItem,
  ItemContent,
  ItemTitle,
  ItemSubtitle,
  Spinner,
} from './AsyncSearchField.style'

import {Input} from '../../../../shared/Input'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {StyledSearchIcon} from '../../../../shared/icons/icons.styles'
import countryRegionData from '../../../../../utils/country-region.json'

// Alternate names Loqate might return for a province/state that don't match
// country-region.json's canonical spelling — map them onto the canonical name
// so the lookup below still resolves instead of silently falling back to the
// select's first option.
const PROVINCE_NAME_ALIASES_BY_COUNTRY = {
  // Italy: provinces have been split/merged/reinstated multiple times by
  // administrative reform (most recently 2025 — Medio Campidano and Ogliastra
  // were re-established after being folded into Sud Sardegna in 2016).
  // country-region.json only lists the current "Sud Sardegna" (SU). Not yet
  // verified against a live Sardinia address through Loqate.
  IT: {
    'sulcis iglesiente': 'sud sardegna',
    'medio campidano': 'sud sardegna',
    ogliastra: 'sud sardegna',
    'olbia-tempio': 'sud sardegna',
    gallura: 'sud sardegna',
  },
  // Philippines: renamed "Occidental/Oriental Mindoro" to "Mindoro
  // Occidental/Oriental" in 2022 (RA 11965/11966); unclear which naming
  // Loqate's dataset uses, so alias both directions defensively.
  PH: {
    'occidental mindoro': 'mindoro occidental',
    'oriental mindoro': 'mindoro oriental',
  },
}

// The State <select>'s option value is the province CODE (see SelectField.js /
// locationAPI.js's fetchSelectedCountryRegionList), not the province name
// Loqate returns in ProvinceName — resolve name -> code or the select silently
// falls back to its first option instead of the one Loqate actually matched.
// Both sides of this comparison are romanized ASCII: ooe-service's
// retrieveAddress() romanizes ProvinceName before it reaches here, and
// country-region.json's ES/IT/VN entries were romanized to match (PH/IN never
// had non-ASCII province names).
function resolveStateCode(countryCode, stateName) {
  if (!stateName) return stateName
  const country = countryRegionData.find(c => c.code === countryCode)

  const findByName = name =>
    country?.regions?.find(r => r.name.toLowerCase() === name.toLowerCase())

  const directMatch = findByName(stateName)
  if (directMatch) return directMatch.code

  const alias =
    PROVINCE_NAME_ALIASES_BY_COUNTRY[countryCode]?.[stateName.toLowerCase()]
  const aliasMatch = alias && findByName(alias)
  if (aliasMatch) return aliasMatch.code

  return stateName
}

// Loqate-backed typeahead for the `address` field, used only for the 5 PoC
// countries (see src/utils/address-provider-countries.js) — everyone else keeps
// the plain text input. Find/Retrieve are proxied through ooe-service so the
// Loqate key is never exposed client-side.
export function AddressSearchField({
  field,
  value,
  country: countryProp,
  values,
  variant,
  size,
  disabled,
  error,

  onBlur = () => {},
  onChange,
}) {
  const client = useAuthClient()

  const [query, setQuery] = useState(value || '')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const wrapperRef = useRef(null)
  // Set right after handleSelect populates the field from a Retrieve response,
  // so the debounce effect below doesn't immediately re-search the address it
  // was just given. Comparing `query` to the `value` prop instead doesn't work —
  // onChange updates the parent's value on every keystroke too, so the prop
  // converges with `query` on the very next render and would block real typing.
  const justSelectedRef = useRef(false)
  const selectedCountry = countryProp || values?.country

  const fetchSuggestions = useCallback(
    async (inputValue, container) => {
      const params = new URLSearchParams({text: inputValue})
      if (container) params.set('container', container)
      if (selectedCountry) params.set('country', selectedCountry)

      const response = await client(
        `accounts/address/find?${params.toString()}`,
      )
      return response || []
    },
    [client, selectedCountry],
  )

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false
      return
    }

    if (query.length < 3) {
      setResults([])
      return
    }

    const timeout = setTimeout(async () => {
      try {
        setLoading(true)
        const data = await fetchSuggestions(query)
        setResults(data)
      } catch (err) {
        console.error(err)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timeout)
  }, [query, fetchSuggestions])

  useEffect(() => {
    const handleOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
        onBlur?.(field.key)
      }
    }

    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [field.key, onBlur])

  const handleSelect = async item => {
    // Container results (postcode/street groupings) need another Find drill-down
    // rather than a Retrieve — Loqate's Find never returns a formatted address
    // for anything but Type === 'Address'.
    if (item.Type !== 'Address') {
      setLoading(true)
      try {
        const data = await fetchSuggestions(query, item.Id)
        setResults(data)
      } catch (err) {
        console.error(err)
        setResults([])
      } finally {
        setLoading(false)
      }
      return
    }

    try {
      setLoading(true)
      const resolved = await client(
        `accounts/address/retrieve?id=${encodeURIComponent(item.Id)}`,
      )

      justSelectedRef.current = true
      onChange(field.key, resolved.street || '')
      if (resolved.city) onChange('city', resolved.city)
      if (resolved.state) {
        onChange('state', resolveStateCode(selectedCountry, resolved.state))
      }
      if (resolved.postalCode) onChange('postCode', resolved.postalCode)

      setQuery(resolved.street || '')
      setResults([])
      setOpen(false)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Wrapper ref={wrapperRef}>
      <Input
        label={field.label}
        placeholder={field.placeholder}
        value={query}
        variant={variant}
        size={size}
        disabled={disabled}
        labelLeftIcon={field.labelLeftIcon}
        leftIcon={<StyledSearchIcon />}
        infoText={field.infoText}
        helperText={field.helperText}
        errorText={error}
        required={field.validators?.includes('required')}
        onFocus={() => setOpen(true)}
        onChange={e => {
          setQuery(e.target.value)
          onChange(field.key, e.target.value)
          setOpen(true)
        }}
      />

      {open && query.length >= 3 && (
        <Dropdown>
          {loading && (
            <LoadingState>
              <Spinner />
              Searching...
            </LoadingState>
          )}

          {!loading &&
            results.map(item => (
              <DropdownItem
                key={item.Id}
                type="button"
                onClick={() => handleSelect(item)}
              >
                <ItemContent>
                  <ItemTitle>{item.Text}</ItemTitle>
                  {item.Description && (
                    <ItemSubtitle>{item.Description}</ItemSubtitle>
                  )}
                </ItemContent>
              </DropdownItem>
            ))}

          {!loading && results.length === 0 && (
            <EmptyState>No results found</EmptyState>
          )}
        </Dropdown>
      )}
    </Wrapper>
  )
}
